const normalizeToken = (value) =>
  String(value || '')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/[–—]/g, '-')
    .toLowerCase()
    .replace(/[^a-z0-9"]+/g, ' ')
    .trim();

const splitTokens = (value) =>
  String(value || '')
    .split(/[,|\n]+/)
    .map((token) => normalizeToken(token))
    .filter(Boolean);

const tokenMatches = (cardToken, selectedToken) =>
  cardToken === selectedToken || cardToken.includes(selectedToken) || selectedToken.includes(cardToken);

const getCardTokens = (card, group) => {
  const key = `filter${group.charAt(0).toUpperCase()}${group.slice(1)}`;
  return splitTokens(card.dataset[key]);
};

const getPrice = (card) => {
  const price = Number.parseFloat(String(card.dataset.price || '').replace(/[^\d.]/g, ''));
  return Number.isFinite(price) ? price : 0;
};

const getFeaturedIndex = (card) => {
  const index = Number.parseInt(card.dataset.featuredIndex || '0', 10);
  return Number.isFinite(index) ? index : 0;
};

const initCollectionProductGrid = (root) => {
  if (!root || root.dataset.ccpgReady === 'true') return;
  root.dataset.ccpgReady = 'true';

  const grid = root.querySelector('[data-ccpg-grid]');
  const cards = Array.from(root.querySelectorAll('[data-ccpg-card]'));
  const filterButtons = Array.from(root.querySelectorAll('[data-filter-option]'));
  const clearButton = root.querySelector('[data-ccpg-clear]');
  const viewButtons = Array.from(root.querySelectorAll('[data-ccpg-view]'));
  const sortControls = Array.from(root.querySelectorAll('[data-ccpg-sort]'));
  const countValues = Array.from(root.querySelectorAll('[data-ccpg-count]'));
  const countLabels = Array.from(root.querySelectorAll('[data-ccpg-count-label]'));
  const emptyState = root.querySelector('[data-ccpg-empty]');
  const mobileFilterButton = root.querySelector('[data-ccpg-mobile-filter]');
  const filterCloseButton = root.querySelector('[data-ccpg-filter-close]');
  const filterBackdrop = root.querySelector('[data-ccpg-filter-backdrop]');
  const defaultView = root.dataset.defaultView || 'list';

  const setView = (view) => {
    root.dataset.view = view;
    viewButtons.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.ccpgView === view));
    });
  };

  const getActiveFilters = () => {
    const active = new Map();

    filterButtons.forEach((button) => {
      if (button.getAttribute('aria-pressed') !== 'true') return;

      const group = button.dataset.filterGroup;
      const value = normalizeToken(button.dataset.filterValue);
      if (!group || !value) return;

      if (!active.has(group)) active.set(group, new Set());
      active.get(group).add(value);
    });

    return active;
  };

  const cardMatchesFilters = (card, activeFilters) => {
    for (const [group, selectedValues] of activeFilters.entries()) {
      const cardTokens = getCardTokens(card, group);
      const matchesGroup = Array.from(selectedValues).some((selectedToken) =>
        cardTokens.some((cardToken) => tokenMatches(cardToken, selectedToken))
      );

      if (!matchesGroup) return false;
    }

    return true;
  };

  const sortCards = () => {
    if (!grid) return;

    const sortValue = sortControls[0]?.value || 'recommended';
    const sortedCards = [...cards].sort((a, b) => {
      if (sortValue === 'price-ascending') return getPrice(a) - getPrice(b);
      if (sortValue === 'price-descending') return getPrice(b) - getPrice(a);
      return getFeaturedIndex(a) - getFeaturedIndex(b);
    });

    sortedCards.forEach((card) => grid.appendChild(card));
  };

  const updateCounts = (visibleCount) => {
    countValues.forEach((count) => {
      count.textContent = String(visibleCount);
    });

    countLabels.forEach((label) => {
      label.textContent = visibleCount === 1 ? root.dataset.productLabelSingular : root.dataset.productLabelPlural;
    });
  };

  const applyState = () => {
    const activeFilters = getActiveFilters();
    const hasActiveFilters = activeFilters.size > 0;
    let visibleCount = 0;

    sortCards();

    cards.forEach((card) => {
      const shouldShow = cardMatchesFilters(card, activeFilters);
      card.hidden = !shouldShow;
      if (shouldShow) visibleCount += 1;
    });

    root.classList.toggle('has-active-filters', hasActiveFilters);
    if (clearButton) clearButton.hidden = !hasActiveFilters;
    if (emptyState) emptyState.hidden = visibleCount !== 0;
    updateCounts(visibleCount);
  };

  const closeFilters = () => {
    root.classList.remove('is-filter-open');
    mobileFilterButton?.setAttribute('aria-expanded', 'false');
  };

  filterButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const isPressed = button.getAttribute('aria-pressed') === 'true';
      button.setAttribute('aria-pressed', String(!isPressed));
      applyState();
    });
  });

  clearButton?.addEventListener('click', () => {
    filterButtons.forEach((button) => button.setAttribute('aria-pressed', 'false'));
    applyState();
  });

  viewButtons.forEach((button) => {
    button.addEventListener('click', () => setView(button.dataset.ccpgView || defaultView));
  });

  sortControls.forEach((control) => {
    control.addEventListener('change', () => {
      sortControls.forEach((otherControl) => {
        if (otherControl !== control) otherControl.value = control.value;
      });
      applyState();
    });
  });

  mobileFilterButton?.addEventListener('click', () => {
    const willOpen = !root.classList.contains('is-filter-open');
    root.classList.toggle('is-filter-open', willOpen);
    mobileFilterButton.setAttribute('aria-expanded', String(willOpen));
  });

  filterCloseButton?.addEventListener('click', closeFilters);
  filterBackdrop?.addEventListener('click', closeFilters);

  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeFilters();
  });

  setView(defaultView);
  applyState();
};

document.querySelectorAll('[data-ccpg]').forEach(initCollectionProductGrid);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-ccpg]').forEach(initCollectionProductGrid);
});
